import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  brand,
  CountUp,
  Empty,
  Eyebrow,
  Link,
  MediaGallery,
  NothingYet,
  Page,
  PageBackdrop,
  soundedPointerHandlers,
  store,
  StoreHero,
  type CapsuleTitle,
  type GalleryItem,
} from '../ui';
import { formatMoney } from '@goodbuild/rules/format';
import type { StoreDescription, StoreLink, TitleMediaItem } from '@api/views';
import { storePlatformLabel } from '@goodbuild/rules/store-links';
import {
  artUrlFor,
  COMING_SOON_LABEL,
  isOnSale,
  titleMediaPosterUrl,
  titleMediaUrl,
  useStorePage,
} from '../catalogue';
import { useApiMutation, type ApiError } from '../api';
import { sessionToken } from '../auth';
import { useSiteConfig } from '../site-config';
import { isLauncher } from '../is-launcher';
import { playSound } from '../ui/sound';
import { column, Loading, NotFound } from './shared';
import { useDocumentTitle } from '../title';

/**
 * A store page.
 *
 * Opens on the title at full width, the way a games store does, then puts the
 * price and the buy action immediately under it. What the game is goes below, in
 * its own column.
 *
 * ## It used to be written by the people reading it
 *
 * This page had two blocks where the description is now — *What players say*,
 * generated from reviews by a model and labelled *we cannot change it or take it
 * down*, and *Player pictures*, screenshots players sent in and voted on. Both
 * are gone, along with the seven-module generator behind the first.
 *
 * A store page answers *what is this game*, and the people who can answer that
 * are the people who made it. Reviews are still collected and still belong on
 * this page as reviews — what has ended is reviews being the thing that
 * describes the product. `lib/store-description.ts` has the other half.
 *
 * ## Buying happens here, not on a page after it
 *
 * There was a `/buy/<slug>` screen between this button and the payment: it
 * showed the price again, said who the merchant of record is, and had a second
 * Buy button. It is gone, and the sentence it existed to carry is in the rail
 * below — Creem is the legal seller and somebody is owed that before they pay,
 * not on a screen they have already decided to leave.
 *
 * So this button starts the checkout and sends the buyer to Creem. Signed out,
 * it goes to the sign-in and comes back here, because a checkout belongs to an
 * account and there is no account yet.
 *
 * **The checkout is created on the click, never on arrival.** Firing it from an
 * effect would open a payment session for anybody who lands on a store page,
 * including somebody who followed a link to look at the price.
 *
 * ## Why buying needs this origin, and not a shared cookie
 *
 * The session is a bearer token in `localStorage` on this origin. The obvious
 * way to let another origin sell — a cookie shared across `*.goodbuild.cc` — is
 * the wrong one, and the reason is worth keeping written down: the API sets
 * `credentials: false` on its CORS policy specifically so that a cookie *cannot*
 * be sent, which makes the whole surface immune to CSRF by construction rather
 * than by a check somebody has to remember. A shared cookie hands that back to
 * buy one hop. `@goodbuild/rules/session-hint` relies on the same property and
 * points here for the argument.
 */
export function GameRoute(): React.ReactElement {
  const params = useParams({ strict: false }) as { slug?: string };
  const slug = params.slug ?? '';

  const query = useStorePage(slug);
  const listing = query.data?.listing;
  useDocumentTitle(listing?.name ?? null);

  const site = useSiteConfig();
  const call = useApiMutation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const prices = query.data?.prices ?? [];
  const art = query.data?.art;
  const description = query.data?.description ?? null;
  const storeLinks = query.data?.storeLinks ?? [];
  const media = query.data?.media ?? [];

  const price = prices.find((entry) => entry.currency === 'EUR') ?? prices[0];
  const onSale = listing !== undefined && isOnSale(listing) && price !== undefined;
  const priceLabel = price ? formatMoney(price.currency, price.amountMinor) : null;
  const comingSoon = listing?.releaseState === 'coming_soon';

  async function buy(): Promise<void> {
    if (!listing) return;

    /*
     * Signed out, the answer is the sign-in and not an error.
     *
     * Back to this page rather than to a checkout: coming back mid-payment
     * would mean starting one on arrival, which is the thing the note above
     * says never to do. They land where they were and press the button they
     * were already pressing.
     */
    if (sessionToken() === null) {
      window.location.href = `/sign-in?redirect=${encodeURIComponent(`/g/${listing.slug}`)}`;
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const { url } = await call<{ url: string }>('/checkout', {
        method: 'POST',
        body: JSON.stringify({ productId: listing.id }),
      });
      // Left busy: the navigation is about to happen, and a button that
      // re-enables for the half second before it does invites a second click
      // and a second checkout session.
      window.location.href = url;
    } catch (cause) {
      // Core's refusals carry a sentence written for a person and index.ts maps
      // them to statuses, so the message is shown rather than replaced with
      // something vaguer.
      setError((cause as ApiError).message || 'That did not start. Try again.');
      playSound('requestRefused');
      setBusy(false);
    }
  }

  const hero: CapsuleTitle | null = listing
    ? {
        slug: listing.slug,
        name: listing.name,
        studio: listing.developerName,
        price: priceLabel,
        artUrl: artUrlFor(art, listing.id, 'capsule_main'),
        portraitArtUrl: artUrlFor(art, listing.id, 'library_capsule'),
        heroArtUrl: artUrlFor(art, listing.id, 'library_hero'),
        pageBackgroundUrl: artUrlFor(art, listing.id, 'page_background'),
        logoUrl: artUrlFor(art, listing.id, 'logo'),
        liveViewers: null,
        releaseLabel: comingSoon ? COMING_SOON_LABEL : null,
      }
    : null;

  if (query.isPending) {
    return (
      <Page site="store">
        <Loading label="Loading the game" />
      </Page>
    );
  }

  if (query.error && query.error.status !== 404) {
    return (
      <Page site="store">
        <Empty>{query.error.message}</Empty>
      </Page>
    );
  }

  if (!listing) return <NotFound />;

  return (
    <Page
      site="store"
      backdrop={<PageBackdrop imageUrl={hero?.pageBackgroundUrl ?? null} />}
      bleed={<StoreHero title={hero} tall headingLevel="h1" />}
    >
      <div className={brand.g2 ?? ''} style={{ alignItems: 'start' }}>
        <div style={column(6)}>
          <header style={column(2)}>
            <Eyebrow>{listing.developerName}</Eyebrow>
          </header>

          <MediaGallery items={galleryItemsFor(listing.id, media)} gameName={listing.name} />

          <StoreDescriptionBlock name={listing.name} description={description} />
        </div>

        {/* The buy rail. Sticky on wide screens so the price stays reachable
            while a reader scrolls the description. */}
        <aside className={store.buyRail ?? ''}>
          <div className={store.buyCard ?? ''}>
            {comingSoon ? (
              <span className={store.buyPrice ?? ''}>{COMING_SOON_LABEL}</span>
            ) : price ? (
              <CountUp
                to={price.amountMinor}
                format={(amount) => formatMoney(price.currency, Math.round(amount))}
                className={store.buyPrice ?? ''}
              />
            ) : (
              <span className={store.buyPrice ?? ''}>No price yet</span>
            )}

            {onSale && priceLabel ? (
              <>
                <button
                  type="button"
                  className={store.buyAction ?? ''}
                  onClick={() => void buy()}
                  disabled={busy}
                  {...soundedPointerHandlers}
                >
                  {busy ? 'Starting…' : `Buy ${priceLabel}`}
                </button>

                {/*
                  Who the buyer is actually buying from, before they pay rather
                  than after. It was the only thing on the removed `/buy` screen
                  that was not a repeat of this rail, and it is the one thing on
                  it somebody is owed: Creem is the merchant of record, so Creem
                  bills them, adds their tax and sends the receipt, and Creem is
                  the name that turns up on the card statement.
                */}
                {site.data ? (
                  <p className={store.buyNote ?? ''}>
                    {site.data.traderLine} They take the payment on the next screen, add the tax for
                    your address, and send the receipt.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="gb-body" style={{ margin: 0 }}>
                {listing.releaseState === 'coming_soon' ? 'Not out yet.' : 'Not for sale.'}
              </p>
            )}

            {error ? (
              <p className="gb-body" role="alert" style={{ margin: 0 }}>
                {error}
              </p>
            ) : null}

            <dl className={store.factList ?? ''}>
              <dt>Made by</dt>
              <dd>{listing.developerName}</dd>
              <dt>Status</dt>
              <dd>{listing.releaseState.replace(/_/g, ' ')}</dd>
            </dl>

            <PlaysInTheLauncher productType={listing.type} />

            <AlsoAvailableOn links={storeLinks} />
          </div>
        </aside>
      </div>
    </Page>
  );
}

function galleryItemsFor(productId: string, media: readonly TitleMediaItem[]): GalleryItem[] {
  return media.map((item) => ({
    id: item.id,
    kind: item.kind,
    url: titleMediaUrl(productId, item.id),
    posterUrl: item.hasPoster ? titleMediaPosterUrl(productId, item.id) : null,
  }));
}

const PRODUCT_TYPES_THE_LAUNCHER_INSTALLS = new Set([
  'base_game',
  'dlc',
  'bundle',
  'demo',
  'playtest_build',
]);

function PlaysInTheLauncher({ productType }: { productType: string }): React.ReactElement | null {
  if (!PRODUCT_TYPES_THE_LAUNCHER_INSTALLS.has(productType)) return null;

  if (isLauncher()) {
    return (
      <div className={store.alsoOn ?? ''}>
        <h3 className={store.alsoOnTitle ?? ''}>How you play it</h3>
        <p className={store.buyNote ?? ''}>
          You are in the launcher. Buy it and it turns up in your library, ready to install.
        </p>
      </div>
    );
  }

  return (
    <div className={store.alsoOn ?? ''}>
      <h3 className={store.alsoOnTitle ?? ''}>How you play it</h3>
      <p className={store.buyNote ?? ''}>
        This game downloads and runs in the GOODBUILD launcher. It is free, it keeps the game up to
        date, and buying here puts the game straight in it.
      </p>
      <Link className={store.storeLink ?? ''} href="/download" {...soundedPointerHandlers}>
        Get the launcher
      </Link>
    </div>
  );
}

/**
 * What the game is, with the formatting it was written with.
 *
 * **This renders markup, and the safety of doing so lives entirely in
 * `apps/api/src/lib/description-html.ts`.** The body is sanitised at the moment
 * it is written — there is one writer, `lib/store-description.ts`, and it puts
 * both imported markup and staff-typed prose through the same function, which
 * re-emits them from a fixed table of tags rather than filtering what it was
 * given.
 *
 * So the rule for this component is short and has no exceptions: **never render
 * a string here that did not come out of that sanitiser.** Not a description
 * from another endpoint, not one assembled in the client, not one a query
 * parameter contributed to. If a second source of description text ever appears,
 * it goes through the same gate or it does not reach this line.
 *
 * The alternative was reducing Steam's HTML to plain text, which is what this
 * did first. It is safer and it is visibly not the page the studio wrote: the
 * headings, the lists and the screenshots are most of what a store description
 * *is*, and a wall of undifferentiated prose reads as a broken import.
 *
 * An empty state rather than a hidden section. A store page with nothing where
 * the description goes reads as broken; one that says nobody has written it yet
 * reads as true, which it is.
 */
function StoreDescriptionBlock({
  name,
  description,
}: {
  name: string;
  description: StoreDescription | null;
}): React.ReactElement {
  const body = description?.body.trim() ?? '';

  return (
    <section style={column(3)}>
      <h2 className="gb-subhead">About this game</h2>

      {body === '' ? (
        <NothingYet headline="No description yet">
          <p className="gb-body" style={{ margin: 0 }}>
            Nobody has written one for {name} yet.
          </p>
        </NothingYet>
      ) : (
        <div
          className={store.description ?? ''}
          // Sanitised on write, by the one function allowed to produce this
          // column. See the note above before touching this line.
          dangerouslySetInnerHTML={{ __html: body }}
        />
      )}
    </section>
  );
}

/**
 * Where else the game is sold, in the buy card rather than under the
 * description.
 *
 * **It belongs beside the price because it answers a question about buying.**
 * Under the description it read as an afterthought at the bottom of a long
 * column, which somebody deciding where to buy has already scrolled past. In the
 * card it sits under the facts, in the one part of the page that stays put while
 * the description scrolls.
 *
 * **Absent entirely when there is nothing in it**, rather than a heading over an
 * empty row. Most titles have no other shop, and a card carrying *Also available
 * on* with nothing under it reads as something that failed to load.
 *
 * **These leave our origin, so they carry no referrer.** `noreferrer` keeps the
 * page somebody came from out of another company's analytics, and `noopener` is
 * the ordinary protection alongside it. They are not `target="_blank"`: a shop
 * link is a place you are going, not a footnote, and whether to keep this tab is
 * the reader's decision rather than ours.
 *
 * The order is `STORE_PLATFORM_ORDER`, applied server-side in `readStoreLinks`,
 * so the list does not rearrange itself when a link is added.
 */
function AlsoAvailableOn({ links }: { links: StoreLink[] }): React.ReactElement | null {
  if (links.length === 0) return null;

  return (
    <div className={store.alsoOn ?? ''}>
      <h3 className={store.alsoOnTitle ?? ''}>Also available on</h3>

      <ul className={store.storeLinks ?? ''}>
        {links.map((link) => (
          <li key={link.platform}>
            <a
              className={store.storeLink ?? ''}
              href={link.url}
              rel="noopener noreferrer"
              {...soundedPointerHandlers}
            >
              {storePlatformLabel(link.platform)}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
